import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, RefreshCw, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SITE_ORIGIN } from '@/seo/siteConfig';

const eenOpEenFaq = [
  {
    id: 'wat-is-1-op-1',
    question: 'Wat is 1-op-1 systemische coaching?',
    answer:
      '1-op-1 systemische coaching is individuele begeleiding waarbij we systemische werkvormen gebruiken, zoals tafelopstellingen of vloerankers, om snel tot de kern van jouw vraagstuk te komen. Het is bedoeld voor leiders en professionals die een hardnekkig patroon in hun rol willen doorbreken, met volledige aandacht voor jouw situatie.',
  },
  {
    id: 'wanneer-geschikt',
    question: 'Wanneer is 1-op-1 coaching geschikt?',
    answer:
      'Als je een concreet vraagstuk hebt in je leiderschap of professionaliteit, als je vastloopt in dezelfde dynamiek, of als je behoefte hebt aan vertrouwelijke begeleiding buiten je team om. Denk aan rolconflicten, loyaliteitsdilemma\'s, burn-out-signalen of het doorbreken van patronen die je al jaren meeneemt.',
  },
  {
    id: 'hoe-werkt-sessie',
    question: 'Hoe werkt een 1-op-1 sessie?',
    answer:
      'We starten met jouw vraag en kijken systemisch naar wat er speelt: welke plek neem je in, welke loyaliteiten zijn actief, wat hoort bij het systeem? Via symbolen of representanten maken we patronen zichtbaar. Een losse sessie duurt circa 1,5 uur. In een traject van 3 tot 5 sessies werken we dieper en verankeren we inzichten in je dagelijks handelen.',
  },
  {
    id: 'plan-sessie',
    question: 'Hoe plan ik een sessie?',
    answer:
      'Neem contact op via het contactformulier of mail naar marthe@systemique.nl. We plannen een kort kennismakingsgesprek om te bepalen of 1-op-1 coaching past bij jouw vraag. Sessies vinden plaats in Castricum of op locatie in de regio Noord-Holland.',
  },
] as const;

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

const EenOpEen = () => {
  return (
    <>
      <SEO 
        title="1-op-1 coaching & opstelling voor leiders"
        description="Individuele systemische coaching en opstellingen in Castricum, Amsterdam en Noord-Holland. Persoonlijke begeleiding voor leiders en professionals met een vraagstuk in hun rol. Losse sessie of traject."
        canonical={`${SITE_ORIGIN}/1-op-1`}
      />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: eenOpEenFaq.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          }),
        }}
      />
      
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
        <div className="absolute inset-0 z-0">
          <GrainTexture opacity={0.2} />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
            </Link>
          </Button>
          
          <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
            1-op-1 <span className="italic font-light font-serif">coaching</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
            Individuele begeleiding voor leiders en professionals.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16 px-6 bg-white">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-6">
            Jouw vraagstuk, <span className="italic font-light font-serif">volledige aandacht</span>
          </h2>
          <p className="text-[#6b4912] text-lg leading-relaxed font-light mb-5">
            Als leider of professional loop je soms vast in patronen die je niet alleen kunt doorbreken.
            In 1-op-1 systemische coaching kijken we naar de onderstroom van jouw vraag: plek, loyaliteit,
            ordening in het systeem waarin je werkt. Via tafelopstellingen of vloerankers maken we die
            dynamiek zichtbaar, vaak met verrassend snel inzicht.
          </p>
          <p className="text-[#6b4912] text-lg leading-relaxed font-light">
            Dit aanbod is onderdeel van het zakelijke traject op organisatieopstellingen.com. Voor
            familieopstellingen als particulier verwijzen we je naar{' '}
            <a
              href="https://www.systemique.nl"
              className="text-[#854C73] underline underline-offset-4 hover:text-[#590D0D]"
              target="_blank"
              rel="noopener noreferrer"
            >
              systemique.nl
            </a>
            .
          </p>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 relative bg-[#FAEBEE]">
        <GrainTexture opacity={0.1} />
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="text-4xl md:text-5xl mb-8 text-center">
            <span className="font-bold" style={{ fontFamily: "'Noto Serif Display', serif", fontWeight: 700, color: '#854C73' }}>Tarieven</span>
          </h2>
          
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-16 text-center max-w-3xl mx-auto">
            Kies een losse sessie voor een concreet vraagstuk, of een traject als je structureel wilt werken aan verandering in je rol.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <SpotlightCard className="p-10">
              <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 bg-[#FAEBEE] rounded-full flex items-center justify-center mb-6">
                  <Target size={32} className="text-[#854C73]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-4 text-center font-bold">
                  Losse sessie
                </h3>
              </div>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-8 text-center flex-grow">
                Voor een specifieke vraag of een direct inzicht. We kijken naar wat er nu speelt en wat er nodig is voor de volgende stap.
              </p>
              
              <div className="border-t border-gray-200 mt-auto pt-6">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Tarief</p>
                  <p className="text-4xl font-serif font-bold text-[#854C73] mb-1">€ 125,-</p>
                  <p className="text-sm text-gray-500">excl. btw · circa 1,5 uur</p>
                </div>
              </div>
            </SpotlightCard>
            
            <SpotlightCard className="p-10">
              <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 bg-[#FAEBEE] rounded-full flex items-center justify-center mb-6">
                  <RefreshCw size={32} className="text-[#854C73]" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-4 text-center font-bold">
                  Traject
                </h3>
              </div>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-8 text-center flex-grow">
                In een traject van 3 tot 5 sessies werken we aan fundamentele verandering en integratie in je dagelijks leven en leiderschap.
              </p>
              
              <div className="border-t border-gray-200 mt-auto pt-6">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Tarief</p>
                  <p className="text-4xl font-serif font-bold text-[#854C73] mb-1">Op aanvraag</p>
                  <p className="text-sm text-gray-500">afgestemd op jouw vraag</p>
                </div>
              </div>
            </SpotlightCard>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24 px-4 sm:px-6 bg-[#FAEBEE]">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#854C73] mb-3">
              Veelgestelde <span className="italic font-light font-serif">vragen</span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-2xl shadow-sm border border-[#854C73]/10 px-4 md:px-6">
            {eenOpEenFaq.map((item) => (
              <AccordionItem key={item.id} value={item.id} className="border-b border-[#854C73]/10 last:border-0">
                <AccordionTrigger className="text-left text-[#854C73] font-serif text-base md:text-lg hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#6b4912] leading-relaxed pt-2 pb-5 text-sm md:text-base">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-16 md:py-20 px-6 bg-[#854C73] text-center">
        <div className="container mx-auto max-w-2xl">
          <h2 className="font-serif text-3xl md:text-4xl text-[#F9EBEF] mb-4">
            Klaar om te <span className="italic font-light">starten?</span>
          </h2>
          <p className="text-[#F9EBEF]/80 text-lg mb-8 font-light">
            Plan een vrijblijvend kennismakingsgesprek. We bespreken je vraag en kijken of 1-op-1 coaching de juiste vorm is.
          </p>
          <Button asChild size="lg" className="bg-[#F9EBEF] text-[#854C73] hover:bg-white rounded-full">
            <Link to="/contact?onderwerp=1-op-1-coaching">Neem contact op</Link>
          </Button>
        </div>
      </section>
    </>
  );
};

export default EenOpEen;
